/**
 * Chat con IA del admin: lógica pura compartida por los asistentes (Guía del portal, Analista
 * de tickets, Agente CEO). Sin React ni Supabase, para que se pruebe sola.
 *
 * Todos los agentes guardan en `ia_conversaciones` / `ia_mensajes`; aquí vive cómo se leen esas
 * filas, cómo se agrupa y filtra el historial y qué pregunta se repite. Lo propio de cada agente
 * (contrato de su función, render de la respuesta) se queda en su módulo.
 */

export interface ConversacionIa {
  /** uuid: lo que piden `ia_mensajes` y `ia_actualizar_conversacion`. */
  id: string;
  /** `ia_conversaciones.numero`: lo que la guía y el analista mandan como `id_conversacion`. */
  numero: number | null;
  titulo: string;
  fijada: boolean;
  /** ISO del último mensaje (o de la creación si no hay). */
  fecha: string;
  /** `false` = de otra persona (compartida conmigo): se lee, no se edita. Por omisión, propia. */
  propia?: boolean;
  /** Visible para Dirección (solo el CEO comparte). */
  compartida?: boolean;
  archivada?: boolean;
  /** Texto bajo el título en lugar de la fecha (p. ej. "Compartida por Ana"). */
  detalle?: string | null;
}

export interface HerramientaIa {
  nombre: string;
}

export interface MensajeIa {
  id: string;
  rol: "usuario" | "asistente";
  texto: string;
  herramientas: HerramientaIa[];
}

export const TITULO_RESPALDO = "Conversación sin título";

/** Tope del nombre de una conversación al renombrarla. */
export const MAX_TITULO = 80;

const esObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const numeroFinito = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/**
 * Filas de `ia_conversaciones` tipadas; descarta las que no traen id o fecha válida. `numero` es
 * opcional (el CEO trabaja por uuid), pero si viene debe ser número.
 */
export function normalizarConversacionesIa(data: unknown): ConversacionIa[] {
  if (!Array.isArray(data)) return [];
  const out: ConversacionIa[] = [];
  for (const f of data) {
    if (!esObjeto(f) || typeof f.id !== "string") continue;
    const numero = typeof f.numero === "string" ? Number(f.numero) : numeroFinito(f.numero);
    if (numero !== null && !Number.isFinite(numero)) continue;
    const fecha =
      typeof f.fecha_ultimo_mensaje === "string"
        ? f.fecha_ultimo_mensaje
        : typeof f.fecha_creacion === "string"
          ? f.fecha_creacion
          : null;
    if (!fecha || Number.isNaN(Date.parse(fecha))) continue;
    const titulo = typeof f.titulo === "string" ? f.titulo.trim() : "";
    out.push({ id: f.id, numero, titulo: titulo || TITULO_RESPALDO, fijada: f.fijada === true, fecha });
  }
  return out;
}

/** Sin acentos ni mayúsculas, para buscar "informe" y encontrar "Informé". */
const normalizarBusqueda = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export function filtrarConversaciones<T extends ConversacionIa>(conversaciones: T[], texto: string): T[] {
  const q = normalizarBusqueda(texto);
  if (!q) return conversaciones;
  return conversaciones.filter((c) => normalizarBusqueda(c.titulo).includes(q));
}

export interface GrupoConversaciones<T extends ConversacionIa = ConversacionIa> {
  titulo: string;
  conversaciones: T[];
}

const inicioDelDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/**
 * Fijadas primero y luego por antigüedad del último mensaje (día local): Hoy, Ayer, Últimos 7
 * días, Este mes, Anteriores. Dentro de cada grupo, la más reciente arriba. Grupos vacíos fuera.
 */
export function agruparConversaciones<T extends ConversacionIa>(conversaciones: T[], ahora: Date): GrupoConversaciones<T>[] {
  const hoy = inicioDelDia(ahora);
  const dia = 86_400_000;
  const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1).getTime();
  const grupos: GrupoConversaciones<T>[] = [
    { titulo: "Fijadas", conversaciones: [] },
    { titulo: "Hoy", conversaciones: [] },
    { titulo: "Ayer", conversaciones: [] },
    { titulo: "Últimos 7 días", conversaciones: [] },
    { titulo: "Este mes", conversaciones: [] },
    { titulo: "Anteriores", conversaciones: [] },
  ];
  const ordenadas = [...conversaciones].sort((a, b) => Date.parse(b.fecha) - Date.parse(a.fecha));
  for (const c of ordenadas) {
    if (c.fijada) {
      grupos[0].conversaciones.push(c);
      continue;
    }
    const t = inicioDelDia(new Date(c.fecha));
    const i = t >= hoy ? 1 : t >= hoy - dia ? 2 : t >= hoy - 6 * dia ? 3 : t >= inicioMes ? 4 : 5;
    grupos[i].conversaciones.push(c);
  }
  return grupos.filter((g) => g.conversaciones.length > 0);
}

/**
 * Reparte el historial en las tres listas del panel: mis conversaciones vigentes (las que se
 * agrupan por fecha), las compartidas conmigo y mis archivadas.
 */
export function separarHistorial<T extends ConversacionIa>(conversaciones: T[]): {
  propias: T[];
  compartidas: T[];
  archivadas: T[];
} {
  const propias: T[] = [];
  const compartidas: T[] = [];
  const archivadas: T[] = [];
  for (const c of conversaciones) {
    if (c.propia === false) compartidas.push(c);
    else if (c.archivada) archivadas.push(c);
    else propias.push(c);
  }
  return { propias, compartidas, archivadas };
}

/** Texto corto de cuándo fue: hora si es de hoy, "Ayer", día y mes, o fecha completa si es de otro año. */
export function cuandoConversacion(iso: string, ahora: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dias = Math.round((inicioDelDia(ahora) - inicioDelDia(d)) / 86_400_000);
  if (dias <= 0) return d.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit" });
  if (dias === 1) return "Ayer";
  if (d.getFullYear() === ahora.getFullYear()) return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" });
}

/** `herramientas` de `ia_mensajes` (jsonb) o de la respuesta de una función: solo los nombres válidos. */
export function sanearHerramientas(data: unknown): HerramientaIa[] {
  if (!Array.isArray(data)) return [];
  const out: HerramientaIa[] = [];
  for (const h of data) {
    if (esObjeto(h) && typeof h.nombre === "string" && h.nombre.trim()) out.push({ nombre: h.nombre.trim() });
  }
  return out;
}

/**
 * Filas de `ia_mensajes` (ya ordenadas por `orden`) → mensajes del panel. Las respuestas con
 * `error` no se pintan (la función tampoco las usa de contexto); la pregunta sí se queda, como
 * en el hilo en vivo.
 */
export function mensajesIaDesdeBitacora(filas: unknown): MensajeIa[] {
  if (!Array.isArray(filas)) return [];
  const out: MensajeIa[] = [];
  for (const f of filas) {
    if (!esObjeto(f) || typeof f.id !== "string" || typeof f.contenido !== "string") continue;
    const texto = f.contenido.trim();
    if (!texto) continue;
    if (f.rol === "usuario") out.push({ id: f.id, rol: "usuario", texto, herramientas: [] });
    else if (f.rol === "asistente" && !f.error) {
      out.push({ id: f.id, rol: "asistente", texto, herramientas: sanearHerramientas(f.herramientas) });
    }
  }
  return out;
}

/**
 * La pregunta que se repite de una conversación: la primera del usuario que no sea un marcador
 * interno (p. ej. el turno del brief del CEO). `null` si no hay ninguna.
 */
export function primeraPregunta(
  mensajes: ReadonlyArray<{ rol: string; texto: string }>,
  ignorar: readonly string[] = [],
): string | null {
  for (const m of mensajes) {
    if (m.rol !== "usuario") continue;
    const t = m.texto.trim();
    if (t && !ignorar.includes(t)) return t;
  }
  return null;
}

/** Nombre nuevo listo para guardar, o `null` si no cambia nada (vacío o igual al actual). */
export function tituloNuevo(valor: string, actual: string): string | null {
  const t = valor.trim().slice(0, MAX_TITULO);
  return t && t !== actual ? t : null;
}

/** El contador del composer aparece al llegar al 80 % del tope (400 de 500 en la guía). */
export function mostrarContador(largo: number, max: number | null | undefined): boolean {
  return !!max && max > 0 && largo >= Math.floor(max * 0.8);
}
